// React Query hooks for the Übergabeprotokoll mobile flow.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  DEFAULT_INVENTORY,
  generateProtocolRef,
  isAdminRecorded,
  PROTOCOL_STORAGE_BUCKET,
  type Protocol,
  type ProtocolAttachment,
  type ProtocolDefect,
  type ProtocolInventoryItem,
  type ProtocolKind,
  type ProtocolPhoto,
  type PhotoCategory,
} from "@/lib/protocol";
import { useAuth } from "@/contexts/AuthContext";
import {
  pickLiveTenantBooking,
  TENANT_LIVE_BOOKING_STATUSES,
  type LiveBookingCandidate,
} from "@/lib/tenant-live-booking";
import { buildRecordedProtocolPdf } from "@/lib/protocol-admin-pdf";
import { isMissingColumn } from "@/hooks/v2/useContractFlowReady";
import { operatorStamped } from "@/lib/operator-stamped";

type BookingLite = {
  id: number;
  property_name: string | null;
  room_number: string | null;
  address: string | null;
  move_in_date?: string | null;
  move_out_date?: string | null;
  /**
   * True when `move_out_date` is the booking's own confirmed date, false
   * when it is `end_date` borrowed as a fallback (the earliest POSSIBLE
   * move-out on an auto-prolonging contract). The move-out "action needed"
   * nag keys off this — see `protocol-action.ts`.
   */
  move_out_confirmed?: boolean;
  deposit_amount?: number | null;
  move_in_protocol_reopened_at?: string | null;
  move_out_protocol_reopened_at?: string | null;
  move_in_protocol_manually_closed_at?: string | null;
  move_out_protocol_manually_closed_at?: string | null;
};

export function useActiveBooking() {
  const { user } = useAuth();
  return useQuery<BookingLite | null>({
    queryKey: ["protocol.active-booking", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          status,
          admin_archived_at,
          contract_signed_at,
          room_number,
          move_in_date,
          move_out_date,
          move_in_protocol_reopened_at,
          move_out_protocol_reopened_at,
          move_in_protocol_manually_closed_at,
          move_out_protocol_manually_closed_at,
          start_date,
          end_date,
          rooms!inner (
            id,
            title,
            properties!inner ( id, title, address )
          )
        `
        )
        .eq("user_id", user!.id)
        // An archived booking is never the tenant's live one. This filter used
        // to be missing here while every other tenant surface had it, and the
        // manual Archive toggle leaves `status` alone — so a hand-archived
        // application still read as `pending`, outranked the real tenancy on
        // `start_date`, and became the booking this page described. An admin
        // reopening the protocol wrote to the real booking, so reopen appeared
        // to do nothing. See `src/lib/tenant-live-booking.ts`.
        .is("admin_archived_at", null)
        // Every stage of an ongoing tenancy. Post-move-in statuses (active /
        // notice_given / move_out_pending) must stay in — "Mark move-in
        // complete" flips a booking to `active`, and the move-out protocol is
        // filled while the tenant is in the notice / move-out stages. Only
        // closed bookings (moved_out / terminated / cancelled) are excluded.
        .in("status", TENANT_LIVE_BOOKING_STATUSES as unknown as string[]);
      if (error) throw error;
      // Ranked, not `.limit(1)`: the choice between several live rows belongs
      // to the shared resolver, so every tenant surface answers it identically.
      const data = pickLiveTenantBooking(
        (rows ?? []) as unknown as LiveBookingCandidate[]
      ) as any;
      if (!data) return null;
      const room = (data as any).rooms as { title?: string | null } | undefined;
      const prop = (room as any)?.properties as
        { title?: string | null; address?: string | null } | undefined;
      const roomNumber =
        ((data as any).room_number as string | null) ??
        (room?.title as string | null) ??
        null;
      return {
        id: (data as any).id as number,
        room_number: roomNumber,
        property_name: (prop?.title as string | null) ?? null,
        address: (prop?.address as string | null) ?? null,
        move_in_date:
          ((data as any).move_in_date as string | null) ??
          ((data as any).start_date as string | null) ??
          null,
        move_out_date:
          ((data as any).move_out_date as string | null) ??
          ((data as any).end_date as string | null) ??
          null,
        move_out_confirmed: !!(data as any).move_out_date,
        move_in_protocol_reopened_at:
          ((data as any).move_in_protocol_reopened_at as string | null) ?? null,
        move_out_protocol_reopened_at:
          ((data as any).move_out_protocol_reopened_at as string | null) ??
          null,
        move_in_protocol_manually_closed_at:
          ((data as any).move_in_protocol_manually_closed_at as
            string | null) ?? null,
        move_out_protocol_manually_closed_at:
          ((data as any).move_out_protocol_manually_closed_at as
            string | null) ?? null,
      };
    },
  });
}

export type MoveOutDateRequest = {
  id: string;
  requested_date: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
};

/**
 * Tenant-requested move-out date (checkout protocol, Neel Slack DM
 * 2026-10-19). The tenant's own pending request for this booking, or null —
 * drives the "Request check-out date" button vs. the "awaiting
 * confirmation" hint on `TenantHandoverRows`.
 */
export function useMyMoveOutDateRequest(bookingId: number | null | undefined) {
  return useQuery<MoveOutDateRequest | null>({
    queryKey: ["protocol.move-out-request", bookingId],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("move_out_date_requests")
        .select("id, requested_date, status, created_at")
        .eq("booking_id", bookingId!)
        .eq("status", "pending")
        .maybeSingle();
      if (error) throw error;
      return (data as MoveOutDateRequest | null) ?? null;
    },
  });
}

/** Files the tenant's requested move-out date for admin approval. */
export function useRequestMoveOutDate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: { bookingId: number; requestedDate: string }) => {
      // user_id is NOT NULL on the table; fail with a clear message here
      // rather than a raw constraint-violation if this ever fires with no
      // session (the button that triggers it is already gated on one).
      if (!user?.id) throw new Error("Not signed in.");
      const { error } = await supabase.from("move_out_date_requests").insert({
        booking_id: args.bookingId,
        user_id: user.id,
        requested_date: args.requestedDate,
      });
      if (error) throw error;
    },
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: ["protocol.move-out-request", args.bookingId],
      });
    },
  });
}

/** Admin: all protocols, newest first, with denormalised booking/tenant info. */
export type AdminProtocolRow = Protocol & {
  bookingId: number;
  propertyTitle: string | null;
  propertyId: number | null;
  roomLabel: string | null;
  roomId: number | null;
  tenantName: string | null;
  tenantEmail: string | null;
};

export function useAdminProtocols() {
  return useQuery<AdminProtocolRow[]>({
    queryKey: ["admin.protocol.list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocols")
        .select(
          `
          *,
          bookings (
            id,
            room_number,
            name,
            email,
            rooms ( id, title, properties ( id, title ) )
          )
        `
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      return rows.map((r) => {
        const booking = r.bookings ?? null;
        const room = booking?.rooms ?? null;
        const prop = room?.properties ?? null;
        return {
          ...r,
          bookingId: booking?.id ?? r.booking_id,
          propertyTitle: prop?.title ?? null,
          propertyId: prop?.id ?? null,
          roomLabel:
            (booking?.room_number as string | null) ?? room?.title ?? null,
          roomId: room?.id ?? null,
          tenantName: booking?.name ?? null,
          tenantEmail: booking?.email ?? null,
        } as AdminProtocolRow;
      });
    },
  });
}

/** Admin: tenant + booking lookup for a single protocol detail view. */
export function useAdminProtocolBundle(protocolId: string | undefined) {
  return useQuery({
    queryKey: ["admin.protocol.bundle", protocolId],
    enabled: !!protocolId,
    queryFn: async () => {
      const [pRes, phRes, invRes, defRes, attRes] = await Promise.all([
        supabase
          .from("handover_protocols")
          .select("*")
          .eq("id", protocolId!)
          .single(),
        supabase
          .from("handover_protocol_photos")
          .select("*")
          .eq("protocol_id", protocolId!)
          .order("category")
          .order("ordering"),
        supabase
          .from("handover_protocol_inventory")
          .select("*")
          .eq("protocol_id", protocolId!)
          .order("ordering"),
        supabase
          .from("handover_protocol_defects")
          .select("*")
          .eq("protocol_id", protocolId!)
          .order("created_at"),
        // COL-425: attachments an admin uploaded when filing this protocol on
        // the tenant's behalf. Only the first ever gets a tenant_documents
        // row (that's the one signature-less submissions need for the
        // Documents tab); the rest live only here, so the detail page is the
        // only place they're visible at all.
        supabase
          .from("handover_protocol_attachments")
          .select("*")
          .eq("protocol_id", protocolId!)
          .order("created_at"),
      ]);
      if (pRes.error) throw pRes.error;
      if (phRes.error) throw phRes.error;
      if (invRes.error) throw invRes.error;
      if (defRes.error) throw defRes.error;
      if (attRes.error) throw attRes.error;
      const protocol = pRes.data as Protocol;

      // Fetch booking + property + tenant inline to render the header.
      const { data: bookingData } = await supabase
        .from("bookings")
        .select(
          `id, room_number, name, email, move_in_date, move_out_date, deposit_amount,
           rooms!inner ( id, title, properties!inner ( id, title, address ) )`
        )
        .eq("id", protocol.booking_id)
        .maybeSingle();
      const bRow = bookingData as any | null;
      const room = bRow?.rooms ?? null;
      const prop = room?.properties ?? null;

      // Deep-link back to the V2 tenant detail page when we have all the
      // ids — used by the breadcrumb on the admin protocol detail view.
      const bookingRoute =
        prop?.id && room?.id && bRow?.id
          ? `/admin/properties/${prop.id}/rooms/${room.id}/tenants/${bRow.id}`
          : null;

      return {
        protocol,
        photos: (phRes.data as ProtocolPhoto[]) ?? [],
        inventory: (invRes.data as ProtocolInventoryItem[]) ?? [],
        defects: (defRes.data as ProtocolDefect[]) ?? [],
        attachments: (attRes.data as ProtocolAttachment[]) ?? [],
        bookingRoute,
        booking: bRow
          ? {
              id: bRow.id as number,
              property_name: prop?.title ?? null,
              property_id: prop?.id ?? null,
              room_id: room?.id ?? null,
              room_number:
                (bRow.room_number as string | null) ?? room?.title ?? null,
              address: prop?.address ?? null,
              tenant_name: bRow.name as string | null,
              tenant_email: bRow.email as string | null,
              move_in_date: bRow.move_in_date as string | null,
              move_out_date: bRow.move_out_date as string | null,
              deposit_amount: bRow.deposit_amount as number | null,
            }
          : null,
      };
    },
  });
}

/** All protocols (any kind / status) attached to a booking, newest first. */
export function useProtocolsForBooking(bookingId: number | null | undefined) {
  return useQuery<Protocol[]>({
    queryKey: ["protocol.list", bookingId],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocols")
        .select("*")
        .eq("booking_id", bookingId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as Protocol[]) ?? [];
    },
  });
}

export function useProtocol(id: string | undefined) {
  return useQuery<Protocol | null>({
    queryKey: ["protocol", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocols")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return (data as Protocol | null) ?? null;
    },
  });
}

export function useProtocolBooking(bookingId: number | null | undefined) {
  return useQuery<BookingLite | null>({
    queryKey: ["protocol.booking", bookingId],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          room_number,
          move_in_date,
          move_out_date,
          deposit_amount,
          rooms!inner ( title, properties!inner ( title, address ) )
        `
        )
        .eq("id", bookingId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const room = (data as any).rooms as { title?: string | null } | undefined;
      const prop = (room as any)?.properties as
        { title?: string | null; address?: string | null } | undefined;
      // Prefer the bookings.room_number column when set; otherwise fall back
      // to the joined room.title (which is the human-readable room label).
      const roomNumber =
        ((data as any).room_number as string | null) ??
        (room?.title as string | null) ??
        null;
      return {
        id: (data as any).id as number,
        move_in_date: (data as any).move_in_date as string | null,
        move_out_date: (data as any).move_out_date as string | null,
        deposit_amount: (data as any).deposit_amount as number | null,
        room_number: roomNumber,
        property_name: (prop?.title as string | null) ?? null,
        address: (prop?.address as string | null) ?? null,
      };
    },
  });
}

/** What a submitted protocol found, in the few numbers a card can carry. */
export type ProtocolSummary = {
  photos: number;
  items: number;
  /** Inventory items NOT in "good" condition, split by how bad. */
  wear: number;
  damaged: number;
  defects: number;
  /** Defects logged as high severity — the ones that decide a deposit. */
  highSeverityDefects: number;
};

/**
 * Counts for the tenant profile's protocol card.
 *
 * The card used to say only "Submitted 14 Aug" — an admin could not tell a
 * clean handover from one with four damaged items without leaving the profile
 * for the full protocol page, so in practice nobody looked. This is the
 * smallest read that makes the card answer "is there anything here?": three
 * narrow selects, no photos or descriptions, no attachments.
 *
 * Exported separately from the hook so the error handling (a failed select
 * throws rather than counting as zero) is testable without react-query.
 */
export async function fetchProtocolSummary(
  protocolId: string
): Promise<ProtocolSummary> {
  const [photos, inventory, defects] = await Promise.all([
    supabase
      .from("handover_protocol_photos")
      .select("id", { count: "exact", head: true })
      .eq("protocol_id", protocolId),
    supabase
      .from("handover_protocol_inventory")
      .select("condition")
      .eq("protocol_id", protocolId),
    supabase
      .from("handover_protocol_defects")
      .select("severity")
      .eq("protocol_id", protocolId),
  ]);
  // A failed count/select must not read as "nothing here" — the card hides
  // the summary entirely on error (see tenant-profile-tabs.tsx's
  // `{summary && (...)}` guard) rather than showing a real handover as a
  // clean one.
  if (photos.error) throw photos.error;
  if (inventory.error) throw inventory.error;
  if (defects.error) throw defects.error;
  const items = (inventory.data ?? []) as Array<{
    condition: string | null;
  }>;
  const defectRows = (defects.data ?? []) as Array<{
    severity: string | null;
  }>;
  return {
    photos: photos.count ?? 0,
    items: items.length,
    wear: items.filter((i) => i.condition === "wear").length,
    damaged: items.filter((i) => i.condition === "damaged").length,
    defects: defectRows.length,
    highSeverityDefects: defectRows.filter((d) => d.severity === "high").length,
  };
}

export function useProtocolSummary(protocolId: string | undefined) {
  return useQuery<ProtocolSummary>({
    queryKey: ["protocol.summary", protocolId],
    enabled: !!protocolId,
    queryFn: () => fetchProtocolSummary(protocolId!),
  });
}

export function useProtocolPhotos(protocolId: string | undefined) {
  return useQuery<ProtocolPhoto[]>({
    queryKey: ["protocol.photos", protocolId],
    enabled: !!protocolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocol_photos")
        .select("*")
        .eq("protocol_id", protocolId!)
        .order("category")
        .order("ordering");
      if (error) throw error;
      return (data as ProtocolPhoto[]) ?? [];
    },
  });
}

export function useProtocolInventory(protocolId: string | undefined) {
  return useQuery<ProtocolInventoryItem[]>({
    queryKey: ["protocol.inventory", protocolId],
    enabled: !!protocolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocol_inventory")
        .select("*")
        .eq("protocol_id", protocolId!)
        .order("ordering");
      if (error) throw error;
      return (data as ProtocolInventoryItem[]) ?? [];
    },
  });
}

/**
 * Should this protocol get a default inventory checklist seeded into it?
 *
 * A protocol row can exist with NO inventory rows: `autoSubmitProtocol` (the
 * reminder tick) inserts the row to record that a window closed unsubmitted,
 * and it has no checklist to record — only `useCreateProtocol`, the tenant's
 * own start, seeds `DEFAULT_INVENTORY`.
 *
 * That row becomes a walkthrough the moment an admin reopens the protocol and
 * the tenant resumes it, and an empty checklist is a dead end rather than a
 * cosmetic gap: `isInventoryComplete([])` is false, so the Continue button
 * never unlocks and there is not a single row on screen to fill in. Seeding
 * here rather than in the tick keeps the tick's auto-accepted PDF honest — it
 * must not print a checklist nobody filled in — and self-heals any protocol
 * missing its inventory, whatever created it.
 */
export function shouldSeedInventory(args: {
  status: string | null | undefined;
  inventoryCount: number;
  isLoading: boolean;
  alreadyFailed?: boolean;
}): boolean {
  if (args.isLoading || args.alreadyFailed) return false;
  // Only a draft is still being filled in. Never touch a submitted protocol.
  if (args.status !== "draft") return false;
  return args.inventoryCount === 0;
}

/**
 * Seeds `DEFAULT_INVENTORY` into `protocolId`'s checklist if it has none.
 * Returns whether it actually inserted anything.
 *
 * The leading read is a cheap short-circuit for the common case, not the
 * race guard — two tabs opening the same resumed draft can both pass it
 * before either has inserted. The unique index on `(protocol_id, label)`
 * (migration 20260828230000) plus `ignoreDuplicates` below is what actually
 * makes this safe to run twice concurrently: a losing write is dropped
 * instead of duplicating the checklist.
 */
export async function ensureProtocolInventorySeed(
  protocolId: string
): Promise<boolean> {
  const { data: existing, error: readError } = await supabase
    .from("handover_protocol_inventory")
    .select("id")
    .eq("protocol_id", protocolId)
    .limit(1);
  if (readError) throw readError;
  if (existing?.length) return false;

  const rows = DEFAULT_INVENTORY.map((it) => ({
    ...it,
    protocol_id: protocolId,
  }));
  const { error } = await supabase
    .from("handover_protocol_inventory")
    .upsert(rows, {
      onConflict: "protocol_id,label",
      ignoreDuplicates: true,
    });
  if (error) throw error;
  return true;
}

/** Seeds `DEFAULT_INVENTORY` into a draft that has no inventory rows. */
export function useEnsureProtocolInventory(protocolId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => ensureProtocolInventorySeed(protocolId!),
    onSuccess: (seeded) => {
      if (!seeded) return;
      queryClient.invalidateQueries({
        queryKey: ["protocol.inventory", protocolId],
      });
    },
  });
}

export function useProtocolDefects(protocolId: string | undefined) {
  return useQuery<ProtocolDefect[]>({
    queryKey: ["protocol.defects", protocolId],
    enabled: !!protocolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("handover_protocol_defects")
        .select("*")
        .eq("protocol_id", protocolId!)
        .order("created_at");
      if (error) throw error;
      return (data as ProtocolDefect[]) ?? [];
    },
  });
}

export function useCreateProtocol() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: {
      bookingId: number;
      kind: ProtocolKind;
    }): Promise<Protocol> => {
      const ref = generateProtocolRef();
      const { data, error } = await supabase
        .from("handover_protocols")
        .insert({
          ref,
          booking_id: args.bookingId,
          user_id: user?.id ?? null,
          kind: args.kind,
          status: "draft",
        })
        .select("*")
        .single();
      if (error) throw error;
      const protocol = data as Protocol;

      const rows = DEFAULT_INVENTORY.map((it) => ({
        ...it,
        protocol_id: protocol.id,
      }));
      const { error: invError } = await supabase
        .from("handover_protocol_inventory")
        .insert(rows);
      if (invError) throw invError;
      return protocol;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocol.active-booking"] });
      queryClient.invalidateQueries({ queryKey: ["protocol.list"] });
    },
  });
}

export function useUpdateInventoryItem(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      patch: Partial<ProtocolInventoryItem>;
    }) => {
      const { error } = await supabase
        .from("handover_protocol_inventory")
        .update(args.patch)
        .eq("id", args.id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["protocol.inventory", protocolId],
      }),
  });
}

export function useAddPhoto(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      category: PhotoCategory;
      ordering: number;
    }) => {
      const filename = `${crypto.randomUUID()}.${(args.file.name.split(".").pop() || "jpg").toLowerCase()}`;
      const path = `${protocolId}/${args.category}/${filename}`;
      const { error: upErr } = await supabase.storage
        .from(PROTOCOL_STORAGE_BUCKET)
        .upload(path, args.file, {
          upsert: false,
          contentType: args.file.type || "image/jpeg",
        });
      if (upErr) throw upErr;
      const { error: dbErr } = await supabase
        .from("handover_protocol_photos")
        .insert({
          protocol_id: protocolId,
          category: args.category,
          storage_path: path,
          ordering: args.ordering,
        });
      if (dbErr) throw dbErr;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["protocol.photos", protocolId],
      }),
  });
}

export function useRemovePhoto(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (photo: ProtocolPhoto) => {
      await supabase.storage
        .from(PROTOCOL_STORAGE_BUCKET)
        .remove([photo.storage_path]);
      const { error } = await supabase
        .from("handover_protocol_photos")
        .delete()
        .eq("id", photo.id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["protocol.photos", protocolId],
      }),
  });
}

export function useAddDefect(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      area: ProtocolDefect["area"];
      description: string;
      severity: ProtocolDefect["severity"];
      file?: File | null;
    }) => {
      let path: string | null = null;
      if (args.file) {
        const filename = `${crypto.randomUUID()}.${(args.file.name.split(".").pop() || "jpg").toLowerCase()}`;
        path = `${protocolId}/defect/${filename}`;
        const { error: upErr } = await supabase.storage
          .from(PROTOCOL_STORAGE_BUCKET)
          .upload(path, args.file, {
            upsert: false,
            contentType: args.file.type || "image/jpeg",
          });
        if (upErr) throw upErr;
      }
      const { error } = await supabase
        .from("handover_protocol_defects")
        .insert({
          protocol_id: protocolId,
          area: args.area,
          description: args.description,
          severity: args.severity,
          photo_storage_path: path,
        });
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["protocol.defects", protocolId],
      }),
  });
}

export function useSubmitProtocol(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      signatureDataUrl: string;
      language?: "en" | "de";
    }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("handover_protocols")
        .update({
          status: "submitted",
          signature_data: args.signatureDataUrl,
          signed_at: now,
          submitted_at: now,
        })
        .eq("id", protocolId);
      if (error) throw error;
      // Note: the confirmation email is sent from DoneStep after the PDF
      // is rendered + uploaded to storage (so the email can attach it).
      // Keeping that step out of this mutation means submit doesn't block
      // on the multi-second photo render + upload. See `useFinalizeProtocol`.
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocol", protocolId] });
      queryClient.invalidateQueries({ queryKey: ["protocol.list"] });
      queryClient.invalidateQueries({ queryKey: ["admin.protocol.list"] });
    },
  });
}

/**
 * Render + upload the protocol PDF, then trigger send-protocol-email so
 * the confirmation includes the PDF as a Resend attachment. Idempotent:
 * the upload is skipped if `pdf_storage_path` is already set on the row,
 * and the email is skipped if `email_sent_at` is set.
 *
 * Called from DoneStep on mount after the protocol has been submitted.
 * (COL-211 / COL-214)
 */
export function useFinalizeProtocol(protocolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      pdfBlob: Blob;
      pdfFilename: string;
      language: "en" | "de";
      existingPdfPath: string | null;
      alreadyEmailed: boolean;
    }) => {
      let pdfPath = args.existingPdfPath;

      if (!pdfPath) {
        const targetPath = `pdfs/${protocolId}.pdf`;
        const { error: upErr } = await supabase.storage
          .from(PROTOCOL_STORAGE_BUCKET)
          .upload(targetPath, args.pdfBlob, {
            cacheControl: "3600",
            upsert: true,
            contentType: "application/pdf",
          });
        if (upErr) throw upErr;
        const { error: rowErr } = await supabase
          .from("handover_protocols")
          .update({ pdf_storage_path: targetPath })
          .eq("id", protocolId);
        if (rowErr) throw rowErr;
        pdfPath = targetPath;
      }

      // File the finished protocol into the tenant's document list — best
      // effort, since a failure here must never leave a submitted protocol
      // without its PDF. Deliberately NOT nested inside the `!pdfPath`
      // upload branch above: `pdfPath` is set from `existingPdfPath` on
      // every later mount once the upload has succeeded once, so a failure
      // here on the first attempt would otherwise never get a retry. Guarded
      // by an existence check instead, so re-running is idempotent.
      try {
        const fileUrl = `${PROTOCOL_STORAGE_BUCKET}/${pdfPath}`;
        const { data: existingDoc } = await supabase
          .from("tenant_documents")
          .select("id")
          .eq("file_url", fileUrl)
          .maybeSingle();
        if (!existingDoc) {
          const { data: protocolRow } = await supabase
            .from("handover_protocols")
            .select("booking_id, user_id, kind")
            .eq("id", protocolId)
            .maybeSingle();
          const row = protocolRow as {
            booking_id: number;
            user_id: string | null;
            kind: ProtocolKind;
          } | null;
          if (row?.user_id) {
            await supabase.from("tenant_documents").insert(
              operatorStamped<"tenant_documents">({
                user_id: row.user_id,
                booking_id: row.booking_id,
                document_type:
                  row.kind === "move_in"
                    ? "move_in_protocol"
                    : "move_out_protocol",
                title:
                  row.kind === "move_in"
                    ? "Move-in protocol"
                    : "Move-out protocol",
                // Bucket-qualified so TenantDocumentsSection's resolver signs
                // it against `handover-protocols`, not the default
                // tenant-documents bucket where this path doesn't exist.
                file_url: fileUrl,
                file_name: args.pdfFilename,
                status: "approved",
                source: "tenant",
                notes: null,
              })
            );
          }
        }
      } catch (docErr) {
        console.warn(
          "Failed to file protocol PDF into tenant_documents (non-blocking)",
          docErr
        );
      }

      if (!args.alreadyEmailed) {
        // Best-effort email — if Resend fails the row stays unstamped and
        // a later DoneStep mount (or an admin re-send action) can retry.
        try {
          const { error: invokeErr } = await supabase.functions.invoke(
            "send-protocol-email",
            { body: { protocolId, language: args.language } }
          );
          if (invokeErr) throw invokeErr;
          await supabase
            .from("handover_protocols")
            .update({ email_sent_at: new Date().toISOString() })
            .eq("id", protocolId);
        } catch (emailErr) {
          console.warn("send-protocol-email failed (non-blocking)", emailErr);
        }
      }
      return { pdfPath };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocol", protocolId] });
    },
  });
}

export type RecordProtocolArgs = {
  bookingId: number;
  /** The booking's tenant, so the row is owned the way a tenant row is. */
  tenantUserId: string | null;
  kind: ProtocolKind;
  files: File[];
  /** Date of the walkthrough being recorded. Backdated for a stale move-out. */
  walkthroughDate: string;
  note: string;
  /** Falls back to the signed-in admin's name, then their email. */
  recordedByName?: string | null;
  booking: {
    property_name: string | null;
    room_number: string | null;
    address: string | null;
  } | null;
  tenant: { name: string | null; email: string | null };
  /** Off by default for a first recording, on for a replacement — see RecordProtocolDialog. */
  notifyTenant: boolean;
  language?: "en" | "de";
  /**
   * Replace the document on this operator-recorded protocol instead of filing a
   * new one. The row keeps its id, ref and storage path; only the PDF, the
   * attribution and the note change. Refuses anything an operator did not
   * record — a tenant's own signed submission is never overwritten from here.
   */
  replaceProtocolId?: string;
};

export type RecordProtocolResult = {
  protocolId: string;
  ref: string;
  /** False when the database predates 20260818100000 and the attribution
   *  columns could not be written. The PDF still carries the attestation. */
  attributionStored: boolean;
  /**
   * False when the PDF is on file but could not be filed into
   * `tenant_documents` — the protocol is recorded and the deposit bundle
   * unblocked, but the document lists (owner portal, tenant profile, the
   * tenant's own documents) will not show it until someone retries via
   * `useRefileProtocolDocument` (protocolId is always returned so a caller
   * can offer that retry).
   */
  documentFiled: boolean;
};

/**
 * File a recorded protocol's PDF into `tenant_documents` — the write both
 * `useRecordProtocolOnBehalf` and `useRefileProtocolDocument` need, kept in
 * one place so a retry files the exact same row shape as the original
 * attempt. Idempotent on `file_url`, so calling this twice for the same PDF
 * never double-files it.
 */
async function fileRecordedProtocolDocument(params: {
  bookingId: number;
  tenantUserId: string | null;
  kind: ProtocolKind;
  pdfPath: string;
  ref: string;
  note: string | null;
}): Promise<void> {
  const fileUrl = `${PROTOCOL_STORAGE_BUCKET}/${params.pdfPath}`;
  const { data: existingDoc } = await supabase
    .from("tenant_documents")
    .select("id")
    .eq("file_url", fileUrl)
    .maybeSingle();
  if (existingDoc) return;
  const { error: docErr } = await supabase.from("tenant_documents").insert(
    operatorStamped<"tenant_documents">({
      booking_id: params.bookingId,
      user_id: params.tenantUserId,
      document_type:
        params.kind === "move_in" ? "move_in_protocol" : "move_out_protocol",
      title:
        params.kind === "move_in" ? "Move-in protocol" : "Move-out protocol",
      // Bucket-qualified so resolveTenantDocumentUrl signs it against
      // `handover-protocols`, not the default tenant-documents bucket where
      // this path does not exist.
      file_url: fileUrl,
      file_name: `${params.ref}.pdf`,
      status: "approved",
      // NOT 'tenant': this is CoLiviq's record, not the tenant's own account
      // of the room, and the owner feed tags the row from here.
      source: "admin",
      notes: params.note || null,
    })
  );
  if (docErr) throw docErr;
}

/**
 * Tell the people who should know a protocol was just filed or replaced.
 *
 * Always invokes, regardless of `notifyTenant`: send-protocol-email also fires
 * the CoLiviq admin copy and the owner "Protocol Available" mail, and both must
 * go out even when the admin chose not to copy the tenant. `notifyTenant` is
 * passed through so only the tenant-facing email inside send-protocol-email is
 * gated on it. Best-effort, exactly as useFinalizeProtocol treats it: a failed
 * email must not undo a protocol that is now on file.
 */
async function sendProtocolFiledEmails(params: {
  protocolId: string;
  notifyTenant: boolean;
  language?: "en" | "de";
}): Promise<void> {
  try {
    const { error: invokeErr } = await supabase.functions.invoke(
      "send-protocol-email",
      {
        body: {
          protocolId: params.protocolId,
          language: params.language ?? "de",
          notifyTenant: params.notifyTenant,
        },
      }
    );
    if (invokeErr) throw invokeErr;
    if (params.notifyTenant) {
      await supabase
        .from("handover_protocols")
        .update({ email_sent_at: new Date().toISOString() })
        .eq("id", params.protocolId);
    }
  } catch (emailErr) {
    console.warn("send-protocol-email failed (non-blocking)", emailErr);
  }
}

/**
 * Why a replacement was refused. The caller maps `code` to a translated message
 * (a hook has no `t`); `message` is the English text, kept for logs and tests.
 */
export class ReplaceProtocolError extends Error {
  constructor(
    public readonly code:
      | "not_found"
      | "no_document"
      | "not_recorded"
      | "bundle_generated"
      | "partial",
    message: string
  ) {
    super(message);
    this.name = "ReplaceProtocolError";
  }
}

/**
 * Swap the document on a protocol an operator already recorded.
 *
 * In place, deliberately: the same `handover_protocols` row, the same ref and
 * the same storage path. Everything that READS this protocol by reference — the
 * owner portal feed, the tenant's document list, the link in the owner's earlier
 * email — therefore follows the new document with no re-pointing, and there is
 * never a second "submitted" row for the same kind for a resolver to choose
 * between. The price is that the previous PDF is overwritten; the audit log the
 * caller writes is the record of who replaced it and when.
 *
 * What does NOT follow: a deposit bundle that was already assembled. It embeds
 * the PDF bytes as they were (`collectBundleSources`), so it keeps the previous
 * document until it is rebuilt. The caller warns about that in the confirm.
 *
 * `handover_protocol_attachments` rows (evidence filed through "Mark move-in
 * complete") are left alone on purpose: deleting database rows an admin did not
 * ask to delete is not part of swapping a PDF, and the files stay in storage.
 * The protocol detail page therefore still lists them, unlabelled, beside the
 * new note — admin-only, and only for a row filed that way.
 *
 * Only an operator recording can be replaced. A walkthrough the tenant signed,
 * or the tick's deemed-accepted placeholder, is refused: overwriting the
 * former would destroy the tenant's own attested account, and the latter is
 * reopened rather than replaced.
 */
async function replaceRecordedProtocol(
  args: RecordProtocolArgs & { replaceProtocolId: string },
  actor: { userId: string | null; recordedByName: string }
): Promise<RecordProtocolResult> {
  const { data, error: loadErr } = await supabase
    .from("handover_protocols")
    .select(
      "id, ref, kind, booking_id, status, pdf_storage_path, recorded_by_user_id, recorded_by_name, admin_submitted_by, admin_submitted_note"
    )
    .eq("id", args.replaceProtocolId)
    .maybeSingle();
  if (loadErr) throw loadErr;
  const current = data as {
    id: string;
    ref: string;
    kind: ProtocolKind;
    booking_id: number;
    status: string;
    pdf_storage_path: string | null;
    recorded_by_user_id: string | null;
    recorded_by_name: string | null;
    admin_submitted_by: string | null;
    admin_submitted_note: string | null;
  } | null;

  if (
    !current ||
    current.booking_id !== args.bookingId ||
    current.kind !== args.kind
  ) {
    throw new ReplaceProtocolError(
      "not_found",
      "This protocol could not be found for the tenant. Reload and try again."
    );
  }
  if (current.status !== "submitted" || !current.pdf_storage_path) {
    throw new ReplaceProtocolError(
      "no_document",
      "There is no filed document to replace. Use Record protocol instead."
    );
  }
  if (!isAdminRecorded(current)) {
    throw new ReplaceProtocolError(
      "not_recorded",
      "Only a protocol an operator recorded can be replaced. A tenant's own submission stays as filed."
    );
  }

  // Product decision (Neel, COL-555 thread): once the deposit settlement
  // bundle has been generated for this booking — draft or sent, release
  // implies generated — no protocol may be added or replaced. The dialog
  // already disables Save/Replace for this (`bundleState`), so this is a
  // server-side check behind that UI gate, not the primary guard; the DB
  // trigger `enforce_protocol_replace_restrictions` is the real backstop.
  const { data: settlement, error: settlementErr } = await supabase
    .from("deposit_settlements")
    .select("bundle_generated_at, released_at")
    .eq("booking_id", current.booking_id)
    .maybeSingle();
  if (settlementErr) throw settlementErr;
  const bundleRow = settlement as {
    bundle_generated_at: string | null;
    released_at: string | null;
  } | null;
  if (bundleRow?.bundle_generated_at || bundleRow?.released_at) {
    throw new ReplaceProtocolError(
      "bundle_generated",
      "A deposit settlement bundle has already been generated for this booking. Protocols can no longer be added or replaced."
    );
  }

  const now = new Date().toISOString();
  const pdfBlob = await buildRecordedProtocolPdf({
    ref: current.ref,
    kind: args.kind,
    walkthroughDate: args.walkthroughDate,
    recordedAt: now,
    recordedByName: actor.recordedByName,
    note: args.note,
    booking: args.booking,
    tenant: args.tenant,
    files: args.files,
  });

  // The path already on the row, not a recomputed `pdfs/<id>.pdf`: that is what
  // tenant_documents.file_url and every signed link was derived from.
  const pdfPath = current.pdf_storage_path;
  const { error: upErr } = await supabase.storage
    .from(PROTOCOL_STORAGE_BUCKET)
    .upload(pdfPath, pdfBlob, {
      cacheControl: "3600",
      upsert: true,
      contentType: "application/pdf",
    });
  if (upErr) throw upErr;

  // `recorded_at` moves to now on purpose: it is the FILING moment every
  // reopen comparison reads (`protocolFiledAt`), so a replacement filed after a
  // reopen stands, exactly as a first recording filed after one does.
  //
  // A row that arrived through "Mark move-in complete" also carries
  // admin_submitted_*, which the protocol detail page prints as its own note.
  // Leaving the old pair would show the replaced note beside the new document.
  const patch: Record<string, unknown> = {
    submitted_at: args.walkthroughDate,
    recorded_by_user_id: actor.userId,
    recorded_by_name: actor.recordedByName,
    recorded_at: now,
    admin_note: args.note,
  };
  if (current.admin_submitted_by || current.admin_submitted_note) {
    patch.admin_submitted_by = actor.userId;
    patch.admin_submitted_note = args.note || null;
  }
  const { error: updErr } = await supabase
    .from("handover_protocols")
    .update(patch)
    .eq("id", current.id);
  if (updErr) {
    // The new PDF is already in storage and cannot be put back, so a bare
    // failure would tell the admin nothing changed when the document did. Say
    // so, and name the way out: replacing again is idempotent — it overwrites
    // the same path and writes the record — so it finishes what this started.
    console.error(
      "Replaced protocol PDF was uploaded but the protocol row update failed",
      updErr
    );
    throw new ReplaceProtocolError(
      "partial",
      "The new document was saved, but the protocol record could not be updated. Replace it again to finish."
    );
  }

  // The tenant_documents row already points at this path, so only its note
  // needs to follow. A failure here leaves a stale note on a document that is
  // itself correct, so it is logged but never reported as "not filed" — that
  // flag sends the admin to a retry that cannot fix a note.
  const { error: noteErr } = await supabase
    .from("tenant_documents")
    .update({ notes: args.note || null })
    .eq("file_url", `${PROTOCOL_STORAGE_BUCKET}/${pdfPath}`);
  if (noteErr) {
    console.warn(
      "Failed to refresh the note on the replaced protocol's document (non-blocking)",
      noteErr
    );
  }
  // If the row never got filed in the first place, the idempotent insert below
  // fills the gap — a replacement doubles as the retry. Only THIS failing means
  // the document is missing from the lists.
  let documentFiled = true;
  try {
    await fileRecordedProtocolDocument({
      bookingId: args.bookingId,
      tenantUserId: args.tenantUserId,
      kind: args.kind,
      pdfPath,
      ref: current.ref,
      note: args.note,
    });
  } catch (docErr) {
    console.warn(
      "Failed to file replaced protocol into tenant_documents (non-blocking)",
      docErr
    );
    documentFiled = false;
  }

  await sendProtocolFiledEmails({
    protocolId: current.id,
    notifyTenant: args.notifyTenant,
    language: args.language,
  });

  return {
    protocolId: current.id,
    ref: current.ref,
    attributionStored: true,
    documentFiled,
  };
}

/**
 * File a handover protocol on the tenant's behalf (admin / super_admin).
 *
 * The tenant is normally the only party who can produce one, so a tenant who
 * never does it leaves the journey stuck and the deposit bundle permanently
 * blocked on a missing PDF. This writes the same row the tenant flow writes —
 * same gates, same bundle — from the paperwork the admin holds.
 *
 * The step ORDER is the correctness argument, not an implementation detail:
 * the row is only flipped to `submitted` once its PDF is on file. Submitting
 * first would fire the move-in trigger and advance the journey, and a PDF
 * upload that then failed would leave a submitted-row-with-no-PDF — which is
 * precisely the broken state this feature exists to repair.
 */
export function useRecordProtocolOnBehalf() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (
      args: RecordProtocolArgs
    ): Promise<RecordProtocolResult> => {
      // Denormalised at write time so the card and the PDF can name a person
      // without a profiles join, and so the record survives the FK's
      // ON DELETE SET NULL if that admin's account is ever removed.
      const recordedByName =
        args.recordedByName?.trim() ||
        (user?.user_metadata?.name as string | undefined) ||
        user?.email ||
        "coliviq operator";

      if (args.replaceProtocolId) {
        return replaceRecordedProtocol(
          { ...args, replaceProtocolId: args.replaceProtocolId },
          { userId: user?.id ?? null, recordedByName }
        );
      }

      const { data: existingRows, error: listErr } = await supabase
        .from("handover_protocols")
        .select(
          "id, ref, status, submitted_at, pdf_storage_path, recorded_by_user_id, recorded_by_name"
        )
        .eq("booking_id", args.bookingId)
        .eq("kind", args.kind)
        .order("created_at", { ascending: false });
      if (listErr) throw listErr;
      const existing = (existingRows ?? []) as Array<{
        id: string;
        ref: string;
        status: string;
        submitted_at: string | null;
        pdf_storage_path: string | null;
        recorded_by_user_id: string | null;
        recorded_by_name: string | null;
      }>;

      // An operator-recorded row that already has a PDF is what Replace
      // exists for — refuse an insert that would otherwise leave two
      // competing operator recordings for the same kind. A TENANT's own
      // submitted+PDF row does not refuse here: Neel's product rule (COL-555
      // thread) is that an admin can only ever replace an operator's own
      // recording, never a tenant's, so when the filed document is the
      // tenant's own, "Record protocol…" files an ADDITIONAL protocol
      // alongside it rather than being refused — the tenant's row is never
      // touched by this insert.
      if (
        existing.some(
          (r) =>
            r.status === "submitted" && r.pdf_storage_path && isAdminRecorded(r)
        )
      ) {
        throw new Error(
          "An operator has already recorded a protocol for this move. Use Replace to update its document instead."
        );
      }

      // Repair-in-place target: a `submitted` row with no PDF is the artifact
      // the super-admin force-stage path leaves behind, and is exactly what
      // blocks the bundle. Reuse it rather than adding a second submitted row.
      //
      // A tenant `draft` is deliberately NOT reused: it may hold a walkthrough
      // they are part-way through, and submitting their photos under an admin's
      // name would both destroy that work and fire the move-in trigger off it.
      const repairable = existing.find(
        (r) => r.status === "submitted" && !r.pdf_storage_path
      );

      let protocolId: string;
      let ref: string;
      let keptSubmittedAt: string | null = null;
      if (repairable) {
        protocolId = repairable.id;
        ref = repairable.ref;
        keptSubmittedAt = repairable.submitted_at;
      } else {
        ref = generateProtocolRef();
        const { data, error } = await supabase
          .from("handover_protocols")
          .insert({
            ref,
            booking_id: args.bookingId,
            user_id: args.tenantUserId,
            kind: args.kind,
            // Draft until the PDF is up — see the ordering note above.
            status: "draft",
          })
          .select("id")
          .single();
        if (error) throw error;
        protocolId = (data as { id: string }).id;
        // No DEFAULT_INVENTORY seeding: five furniture rows with null
        // conditions would claim a structured walkthrough that never happened.
      }

      const pdfBlob = await buildRecordedProtocolPdf({
        ref,
        kind: args.kind,
        walkthroughDate: args.walkthroughDate,
        recordedAt: new Date().toISOString(),
        recordedByName,
        note: args.note,
        booking: args.booking,
        tenant: args.tenant,
        files: args.files,
      });

      const pdfPath = `pdfs/${protocolId}.pdf`;
      const { error: upErr } = await supabase.storage
        .from(PROTOCOL_STORAGE_BUCKET)
        .upload(pdfPath, pdfBlob, {
          cacheControl: "3600",
          upsert: true,
          contentType: "application/pdf",
        });
      if (upErr) throw upErr;

      const now = new Date().toISOString();
      const base = {
        status: "submitted",
        // A repaired row keeps the timestamp it was force-staged with, so the
        // journey does not appear to move backwards.
        submitted_at: keptSubmittedAt ?? args.walkthroughDate,
        pdf_storage_path: pdfPath,
      };
      const attribution = {
        recorded_by_user_id: user?.id ?? null,
        recorded_by_name: recordedByName,
        recorded_at: now,
        admin_note: args.note,
      };

      let attributionStored = true;
      const { error: updErr } = await supabase
        .from("handover_protocols")
        .update({ ...base, ...attribution })
        .eq("id", protocolId);
      if (updErr) {
        // An environment behind on 20260818100000 must still be able to unblock
        // a deposit. Retry without the attribution columns: the PDF's first page
        // carries the same statement, so the record is degraded, never wrong.
        if (!isMissingColumn(updErr)) throw updErr;
        const { error: retryErr } = await supabase
          .from("handover_protocols")
          .update(base)
          .eq("id", protocolId);
        if (retryErr) throw retryErr;
        attributionStored = false;
      }

      // File it into the tenant's documents, the same way useFinalizeProtocol
      // does for a self-submitted protocol.
      //
      // `handover_protocols` is read only by the protocol cards. Every DOCUMENT
      // surface — the owner portal feed (through the owner_property_documents
      // RPC), the admin tenant profile's Documents tab, the tenant's own
      // document list — reads `tenant_documents`. Without this row the
      // recording exists in storage and is invisible in all three, which is
      // the state an owner reports as "the protocol never arrived".
      //
      // Best-effort: the protocol is submitted and its PDF is up, which is
      // what unblocks the deposit bundle. Throwing here would fail an action
      // that has already largely succeeded. A failure here is NOT recoverable
      // by re-running this mutation — the "already submitted with a PDF"
      // refusal above blocks any retry — so the caller must offer
      // `useRefileProtocolDocument` against `protocolId` instead of asking
      // for a re-record.
      let documentFiled = true;
      try {
        await fileRecordedProtocolDocument({
          bookingId: args.bookingId,
          tenantUserId: args.tenantUserId,
          kind: args.kind,
          pdfPath,
          ref,
          note: args.note,
        });
      } catch (docErr) {
        console.warn(
          "Failed to file recorded protocol into tenant_documents (non-blocking)",
          docErr
        );
        documentFiled = false;
      }

      // Always invoked, whether or not the tenant is copied — see
      // sendProtocolFiledEmails for why.
      await sendProtocolFiledEmails({
        protocolId,
        notifyTenant: args.notifyTenant,
        language: args.language,
      });

      return { protocolId, ref, attributionStored, documentFiled };
    },
    onSuccess: (res, args) => {
      queryClient.invalidateQueries({ queryKey: ["protocol.list"] });
      queryClient.invalidateQueries({ queryKey: ["admin.protocol.list"] });
      // A replacement rewrites a row that is already on screen elsewhere (the
      // protocol detail page), so its own keys go stale too.
      queryClient.invalidateQueries({ queryKey: ["protocol", res.protocolId] });
      queryClient.invalidateQueries({
        queryKey: ["admin.protocol.bundle", res.protocolId],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2", "handover-protocols", args.bookingId],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2", "handover-bundles", args.bookingId],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2", "deposit-settlement", args.bookingId],
      });
      // The row this mutation files lands in tenant_documents, which backs the
      // profile's Documents tab — a different key from every protocol query
      // above, so without this the tab keeps showing the list from before the
      // recording. Prefix-matched rather than scoped to (user, booking): the
      // tab's key carries both and either may be null, and a refetch of a
      // sibling tenancy costs one query.
      queryClient.invalidateQueries({ queryKey: ["v2", "tenant-documents"] });
    },
  });
}

/**
 * Retry filing an already-recorded protocol's PDF into `tenant_documents`,
 * for the one failure `useRecordProtocolOnBehalf` cannot self-heal: the PDF
 * uploaded and the protocol reached `submitted`, but the best-effort
 * `tenant_documents` insert failed (`documentFiled: false`). Re-running
 * `useRecordProtocolOnBehalf` on the same booking/kind cannot recover this —
 * its own guard refuses any row that is already `submitted` with a PDF — so
 * this hook re-attempts only the missing step, reading what it needs
 * straight off the `handover_protocols` row rather than requiring the
 * caller to still have the original form args around.
 */
export function useRefileProtocolDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (protocolId: string): Promise<{ filed: boolean }> => {
      // `admin_note` arrived with 20260818100000; every other column here
      // predates it. On a database behind that migration the select errors on
      // the unknown column and takes the whole retry with it — so retry
      // without it and file the document with no note. Same degrade-never-fail
      // fallback useRecordProtocolOnBehalf applies to the attribution columns:
      // an unblocked deposit beats a note.
      const COLUMNS = "booking_id, user_id, kind, pdf_storage_path, ref";
      let data: unknown;
      const first = await supabase
        .from("handover_protocols")
        .select(`${COLUMNS}, admin_note`)
        .eq("id", protocolId)
        .maybeSingle();
      if (first.error) {
        if (!isMissingColumn(first.error)) throw first.error;
        const retry = await supabase
          .from("handover_protocols")
          .select(COLUMNS)
          .eq("id", protocolId)
          .maybeSingle();
        if (retry.error) throw retry.error;
        data = retry.data;
      } else {
        data = first.data;
      }
      const row = data as {
        booking_id: number;
        user_id: string | null;
        kind: ProtocolKind;
        pdf_storage_path: string | null;
        ref: string;
        admin_note?: string | null;
      } | null;
      if (!row?.pdf_storage_path) {
        throw new Error("This protocol has no PDF on file yet.");
      }
      await fileRecordedProtocolDocument({
        bookingId: row.booking_id,
        tenantUserId: row.user_id,
        kind: row.kind,
        pdfPath: row.pdf_storage_path,
        ref: row.ref,
        note: row.admin_note ?? null,
      });
      return { filed: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocol.list"] });
      queryClient.invalidateQueries({ queryKey: ["admin.protocol.list"] });
      // The point of this mutation is the tenant_documents row, so the list
      // that reads it has to refetch. Without this the retry's own success
      // toast ("Filed into the document lists") pointed at a Documents tab
      // still rendering the list from before it.
      queryClient.invalidateQueries({ queryKey: ["v2", "tenant-documents"] });
    },
  });
}

/** Returns a signed URL for a storage path (3600s expiry). */
export async function getSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(PROTOCOL_STORAGE_BUCKET)
    .createSignedUrl(path, 3600);
  if (error || !data) return null;
  return data.signedUrl;
}
